import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { Weekday } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import '@chancenwerk/client/design/components/cr-textarea.js';
import { MarktVeranstaltungsAkquiseEntry } from '@chancenwerk/markt/entities/MarktVeranstaltungsAkquiseEntry.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { DateTime } from 'luxon';

@customElement('markt-dialog-veranstaltung-entry-edit')
export class MarktDialogVeranstaltungEntryEdit extends ChromiumDialog<Maybe<MarktVeranstaltungsAkquiseEntry>> {
  static override get element_name() {
    return 'markt-dialog-veranstaltung-entry-edit';
  }

  override heading?: string | undefined = 'Veranstaltung bearbeiten';

  @property({ attribute: false }) entry = new RXController(this, new MarktVeranstaltungsAkquiseEntry({}));

  override content() {
    return html`
      <cr-flex direction="column" space="md" ${css({ width: '700px' })}>
        <chancen-component-card-title>Details</chancen-component-card-title>
        <cr-flex horizontal space="md">
          <cr-input
            flex
            .value=${this.entry.value.semester}
            placeholder="Semester"
            clearable
            @value-picked=${(e: ValueEvent<Maybe<string>>) => {
              this.entry.value.semester = e.detail.value;
            }}
          ></cr-input>

          <cr-input
            flex
            .value=${this.entry.value.name}
            placeholder="Name"
            clearable
            @value-picked=${(e: ValueEvent<Maybe<string>>) => {
              this.entry.value.name = e.detail.value;
            }}
          ></cr-input>

          <cr-input
            flex
            .value=${this.entry.value.room}
            placeholder="Raum"
            clearable
            @value-picked=${(e: ValueEvent<Maybe<string>>) => {
              this.entry.value.room = e.detail.value;
            }}
          ></cr-input>
        </cr-flex>

        <br />

        <chancen-component-card-title>Zeiten</chancen-component-card-title>

        <cr-dropdown-multiselection
          clearable
          placeholder="Wochentage"
          .value=${this.entry.value.days}
          .selectables=${Object.values(Weekday)}
          @value-picked=${(e: ValueEvent<Weekday[]>) => {
            this.entry.value.days = e.detail.value;
          }}
        ></cr-dropdown-multiselection>

        <cr-flex horizontal space="md">
          <cr-date-picker-dropdown
            flex
            placeholder="Zeitraum Start"
            .end-year=${DateTime.now().plus({ years: 10 }).year}
            click-to-save
            clearable
            .value=${this.entry.value.start}
            @value-changed=${(e: ValueEvent<Maybe<DateTime>>) => {
              this.entry.value.start = e.detail.value;
            }}
          ></cr-date-picker-dropdown>

          <cr-date-picker-dropdown
            flex
            placeholder="Zeitraum Ende"
            .end-year=${DateTime.now().plus({ years: 10 }).year}
            click-to-save
            clearable
            .value=${this.entry.value.end}
            @value-changed=${(e: ValueEvent<Maybe<DateTime>>) => {
              this.entry.value.end = e.detail.value;
            }}
          ></cr-date-picker-dropdown>
        </cr-flex>

        <cr-flex horizontal space="md">
          <cr-input
            type="time"
            flex
            .value=${this.entry.value.startTime}
            placeholder="Startzeit"
            clearable
            @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
              this.entry.value.startTime = e.detail.value;
            }}
          ></cr-input>
          <cr-input
            type="time"
            flex
            .value=${this.entry.value.endTime}
            placeholder="Endzeit"
            clearable
            @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
              this.entry.value.endTime = e.detail.value;
            }}
          ></cr-input>
        </cr-flex>

        <cr-flex justify="end" space="sm">
          <cr-button
            @click=${async () => {
              try {
                await this.close(this.entry.value);
              } catch (e: any) {
                ChromiumSnackbar.show(e.message);
              }
            }}
          >
            Speichern
          </cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }
}
